import { useState, useEffect, useContext } from "react";
import ReusableTable from "../UI/ReusableTable";
import axios from "axios";
import Notification from "../UI/Notification";
import DOMPurify from 'dompurify';
import { Link } from 'react-router-dom';
import Modal from "../UI/Modal";
import InterviewForm from "../Form/InterviewForm";
import { AuthContext } from "../../contexts/AuthContext";
import { getRoleBasedRoute } from "../../utils/routeUtils";

const CampaignDetails = ({ campaign, campaign_id, close }) => {
  const { roles } = useContext(AuthContext);
  const isHR = roles.includes("HR");
  const [activeTab, setActiveTab] = useState("overview");
  const [checklist, setChecklist] = useState([]);
  const [technicalAssessments, setTechnicalAssessments] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [newChecklistItem, setNewChecklistItem] = useState("");
  const [newAssessment, setNewAssessment] = useState({ name: "", link: "" });
  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isInterviewModalOpen, setIsInterviewModalOpen] = useState(false);
  const [selectedCandidateForInterview, setSelectedCandidateForInterview] = useState(null);

  const CHECKLIST_URL = "https://hrmsnew.prepstripe.com/api/checklists/campaign/";
  const ASSESSMENT_URL = "https://hrmsnew.prepstripe.com/api/technical-assessments/campaign/";
  const CANDIDATE = "https://hrmsnew.prepstripe.com/api/candidates/campaign/";

  const fetchChecklist = async () => {
    try {
      const response = await axios.get(`${CHECKLIST_URL}${campaign_id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });
      setChecklist(response.data?.data?.data || []);
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setChecklist(null); 
      } else {
        console.error("Error fetching checklists:", error);
      }
    }
  };
  
  const fetchAssessment = async () => {
    try {
      const response = await axios.get(`${ASSESSMENT_URL}${campaign_id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });
      setTechnicalAssessments(response.data?.data?.data || []);
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setTechnicalAssessments(null); // No assessment found
      } else {
        console.error("Error fetching assessments:", error);
      }
    }
  };
  
  const fetchCandidates = async (page = 1) => {
    try {
      setLoading(true);
      const response = await axios.get(`${CANDIDATE}${campaign_id}?page=${page}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });
      // console.log(response?.data)
      setCandidates(response.data?.data?.data || []);
      setTotalPages(response.data?.data?.last_page || 1); 
      setCurrentPage(response.data?.data?.current_page || 1);
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setCandidates(null); 
      } else {
        console.error("Error fetching candidates:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      fetchCandidates(page)
    }
  };
  
  useEffect(() => {
    if (campaign_id) {
      fetchChecklist();
      fetchAssessment();
      fetchCandidates();
    }
  }, [campaign_id]); 
  

  if (!campaign) {
    return <p className="text-gray-500 p-6"> </p>;
  }

  const addChecklistItem = async () => {
    try {
      const response = await axios.post(
        "https://hrmsnew.prepstripe.com/api/checklists",
        { campaign_id: campaign_id, name: newChecklistItem },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );
      setNotification({
        message: "Item added to the checklist successfully",
        type: "success",
      });
      setChecklist((prev) =>
        Array.isArray(prev) ? [...prev, response.data?.data] : [response.data?.data]
      );
      setNewChecklistItem("");
    } catch (error) {
      setNotification({
        message: "Error adding checklist item",
        type: "error",
      });
    }
  };
  
  const addTechnicalAssessment = async () => {
    try {
      const response = await axios.post('https://hrmsnew.prepstripe.com/api/technical-assessments', {campaign_id: campaign_id, name: newAssessment.name, link: newAssessment.link}, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
        },
      });
      setNotification({
        message: "Item added to the technical assessment successfully",
        type: "success",
      });
      setTechnicalAssessments((prev) =>
        Array.isArray(prev) ? [...prev, response.data?.data] : [response.data?.data]
      );
      setNewAssessment({ name: "", link: "" });
    } catch (error) {
      setNotification({
        message: `Error adding technical assessments:, ${error}`,
        type: "error",
      });
    }
  };

  // to shortlist candidates
  const handleShortlistCandidate = async (candidateId) => {
    try {
      const response = await axios.post('https://hrmsnew.prepstripe.com/api/candidates/status', 
        { id: candidateId, status: "Shortlisted" }, 
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        }
      );
      
      setNotification({
        message: "Candidate shortlisted successfully",
        type: "success",
      });

      const updatedCandidate = response.data?.data;
      
      setCandidates((prevCandidates) =>
        prevCandidates.map((candidate) =>
          candidate.id === candidateId ? { ...candidate, status: "Shortlisted" } : candidate
        )
      );
  
    } catch (error) {
      setNotification({
        message: `Error updating candidate status:, ${error}`,
        type: "error",
      });
    }
  };

  // to reject candidates
  const handleRejectCandidate = async (candidateId) => {
    try {
      const response = await axios.post('https://hrmsnew.prepstripe.com/api/candidates/status', 
        { id: candidateId, status: "Rejected" }, 
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        }
      );
      
      setNotification({
        message: "Candidate rejected successfully",
        type: "success",
      });

      const updatedCandidate = response.data?.data;
      
      setCandidates((prevCandidates) =>
        prevCandidates.map((candidate) =>
          candidate.id === candidateId ? { ...candidate, status: "Rejected" } : candidate
        )
      );
  
    } catch (error) {
      setNotification({
        message: `Error updating candidate status:, ${error}`,
        type: "error",
      });
    }
  };

  // Interview scheduling functions
  const openInterviewModal = (candidate) => {
    setSelectedCandidateForInterview(candidate);
    setIsInterviewModalOpen(true);
  };

  const closeInterviewModal = () => {
    setIsInterviewModalOpen(false);
    setSelectedCandidateForInterview(null);
  };

  const handleInterviewSubmit = async (interviewData) => {
    try {
      const response = await axios.post('https://hrmsnew.prepstripe.com/api/interviews', 
        {
          ...interviewData,
          campaign_id: campaign_id,
          candidate_id: selectedCandidateForInterview.id
        }, 
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        }
      );
      
      setNotification({
        message: "Interview scheduled successfully",
        type: "success",
      });

      closeInterviewModal();
      
      // Optionally refresh candidates or update the candidate status
      fetchCandidates(currentPage);
  
    } catch (error) {
      setNotification({
        message: `Error scheduling interview: ${error.response?.data?.message || error.message}`,
        type: "error",
      });
    }
  };
  
  const renderTabContent = () => {
    switch (activeTab) {
      case "overview":
        return (
          <div className="bg-gradient-to-br from-white to-gray-50 dark:from-gray-900 dark:to-gray-800 shadow-xl border border-gray-100 dark:border-gray-700 p-6 sm:p-8 rounded-xl w-full mx-auto space-y-6">
            {/* Job Description Card */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-600 shadow-sm hover:shadow-md transition-shadow duration-200">
              <h5 className="text-lg font-semibold text-gray-800 dark:text-white mb-3 flex items-center">
                <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                Job Description
              </h5>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-6 border border-gray-200 dark:border-gray-600">
                <div 
                  className="text-gray-700 dark:text-gray-300 leading-relaxed overflow-hidden"
                  dangerouslySetInnerHTML={{ 
                    __html: DOMPurify.sanitize(campaign.job_description, {
                      ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'span', 'div'],
                      ALLOWED_ATTR: ['class', 'style']
                    })
                  }} 
                />
              </div>
            </div>

            {/* Status and Position Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-600 shadow-sm hover:shadow-md transition-shadow duration-200">
                <h5 className="text-lg font-semibold text-gray-800 dark:text-white mb-3 flex items-center">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                  Status & Position
                </h5>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 dark:text-gray-400 font-medium">Status:</span>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${campaign.status === 'Expired' ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200' : 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'}`}>
                      {campaign.status}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 dark:text-gray-400 font-medium">Position:</span>
                    <span className="text-gray-800 dark:text-gray-200 font-semibold">{campaign.position}</span>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-600 shadow-sm hover:shadow-md transition-shadow duration-200">
                <h5 className="text-lg font-semibold text-gray-800 dark:text-white mb-3 flex items-center">
                  <div className="w-2 h-2 bg-purple-500 rounded-full mr-3"></div>
                  CTC
                </h5>
                <div className="text-center">
                  <div className="text-2xl font-semibold text-gray-800 dark:text-white mb-1">
                    ₹{campaign.ctc}
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">CTC</p>
                </div>
              </div>
            </div>

            {/* Duration and Workplace */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-600 shadow-sm hover:shadow-md transition-shadow duration-200">
              <h5 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
                <div className="w-2 h-2 bg-orange-500 rounded-full mr-3"></div>
                Campaign Details
              </h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <span className="text-gray-600 dark:text-gray-400 font-medium block mb-1">Duration:</span>
                  <span className="text-gray-800 dark:text-gray-200">From {campaign.from_date} to {campaign.to_date}</span>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400 font-medium block mb-1">Workplace:</span>
                  <span className="text-gray-800 dark:text-gray-200">
                    {campaign.workplace?.name || campaign.workplace}
                  </span>
                </div>
              </div>
            </div>

            {/* Creator Info and Timestamps */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-700 p-6 rounded-lg border border-blue-200 dark:border-gray-600">
              <h5 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
                <div className="w-2 h-2 bg-indigo-500 rounded-full mr-3"></div>
                Campaign Information
              </h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <span className="text-gray-600 dark:text-gray-400 font-medium block mb-1">Created By:</span>
                  <div className="text-gray-800 dark:text-gray-200">
                    <p className="font-semibold">{campaign.user?.name}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{campaign.user?.email}</p>
                  </div>

                  {/* attached public link */}
                  <span className="text-gray-800 dark:text-gray-400 font-medium block mb-1">Public Link:</span>
                  <span className="text-gray-800 dark:text-gray-200 font-semibold">
                    <Link
                      to={`/campaign/${campaign?.id}`}
                      className="text-blue-600 dark:text-blue-400 hover:underline hover:underline-offset-4"
                      target="_blank"
                      rel="noopener noreferrer">
                      {campaign?.campaign_name}
                    </Link>
                  </span>
                </div>
                <div className="space-y-3">
                  <div>
                    <span className="text-gray-600 dark:text-gray-400 font-medium block mb-1">Created At:</span>
                    <span className="text-gray-800 dark:text-gray-200 text-sm">{new Date(campaign.created_at).toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400 font-medium block mb-1">Last Updated:</span>
                    <span className="text-gray-800 dark:text-gray-200 text-sm">{new Date(campaign.updated_at).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case "summary":
        return (
          <div className="bg-gradient-to-br from-white to-green-50 dark:from-gray-900 dark:to-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 p-6 sm:p-8 w-full mx-auto">
            {/* Header */}
            <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-200 dark:border-gray-600">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                <h4 className="text-2xl font-semibold text-gray-800 dark:text-white">Campaign Summary</h4>
              </div>
              <button
                onClick={() => setActiveTab("overview")}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-2xl transition-colors duration-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full w-8 h-8 flex items-center justify-center">
                ✕
              </button>
            </div>

            {/* Summary Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {/* Total Candidates */}
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-600">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Candidates</p>
                    <p className="text-3xl font-semibold text-blue-600">{candidates?.length || 0}</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">👥</span>
                  </div>
                </div>
              </div>

              {/* Pending Candidates */}
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-600">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Pending</p>
                    <p className="text-3xl font-semibold text-yellow-600">
                      {candidates?.filter(c => c.status === "Pending").length || 0}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-yellow-100 dark:bg-yellow-900 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">⏳</span>
                  </div>
                </div>
              </div>

              {/* Shortlisted Candidates */}
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-600">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Shortlisted</p>
                    <p className="text-3xl font-semibold text-green-600">
                      {candidates?.filter(c => c.status === "Shortlisted").length || 0}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">✅</span>
                  </div>
                </div>
              </div>

              {/* Rejected Candidates */}
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-600">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Rejected</p>
                    <p className="text-3xl font-semibold text-red-600">
                      {candidates?.filter(c => c.status === "Rejected").length || 0}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-red-100 dark:bg-red-900 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">❌</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Detailed Status Breakdown */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600">
                <h5 className="text-lg font-semibold text-gray-800 dark:text-white">Candidate Status Breakdown</h5>
              </div>
              
              <div className="divide-y divide-gray-200 dark:divide-gray-600">
                {/* Pending Section */}
                {candidates?.filter(c => c.status === "Pending").length > 0 && (
                  <div className="p-6">
                    <div className="flex items-center mb-4">
                      <div className="w-3 h-3 bg-yellow-500 rounded-full mr-3"></div>
                      <h6 className="text-lg font-semibold text-gray-800 dark:text-white">Pending Review ({candidates.filter(c => c.status === "Pending").length})</h6>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {candidates.filter(c => c.status === "Pending").map((candidate, index) => (
                        <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <h6 className="font-medium text-gray-800 dark:text-white">{candidate.name}</h6>
                            <span className="px-2 py-1 bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 rounded-full text-xs font-medium">
                              Pending
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">{candidate.email}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Shortlisted Section */}
                {candidates?.filter(c => c.status === "Shortlisted").length > 0 && (
                  <div className="p-6">
                    <div className="flex items-center mb-4">
                      <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                      <h6 className="text-lg font-semibold text-gray-800 dark:text-white">Shortlisted ({candidates.filter(c => c.status === "Shortlisted").length})</h6>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {candidates.filter(c => c.status === "Shortlisted").map((candidate, index) => (
                        <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <h6 className="font-medium text-gray-800 dark:text-white">{candidate.name}</h6>
                            <span className="px-2 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-full text-xs font-medium">
                              Shortlisted
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">{candidate.email}</p>
                          {candidate.interviews?.some(interview => interview.status === "Scheduled") && (
                            <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">📅 Interview Scheduled</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Rejected Section */}
                {candidates?.filter(c => c.status === "Rejected").length > 0 && (
                  <div className="p-6">
                    <div className="flex items-center mb-4">
                      <div className="w-3 h-3 bg-red-500 rounded-full mr-3"></div>
                      <h6 className="text-lg font-semibold text-gray-800 dark:text-white">Rejected ({candidates.filter(c => c.status === "Rejected").length})</h6>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {candidates.filter(c => c.status === "Rejected").map((candidate, index) => (
                        <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <h6 className="font-medium text-gray-800 dark:text-white">{candidate.name}</h6>
                            <span className="px-2 py-1 bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 rounded-full text-xs font-medium">
                              Rejected
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">{candidate.email}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Other Statuses */}
                {candidates?.filter(c => !["Pending", "Shortlisted", "Rejected"].includes(c.status)).length > 0 && (
                  <div className="p-6">
                    <div className="flex items-center mb-4">
                      <div className="w-3 h-3 bg-gray-500 rounded-full mr-3"></div>
                      <h6 className="text-lg font-semibold text-gray-800 dark:text-white">Other Statuses ({candidates.filter(c => !["Pending", "Shortlisted", "Rejected"].includes(c.status)).length})</h6>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {candidates.filter(c => !["Pending", "Shortlisted", "Rejected"].includes(c.status)).map((candidate, index) => (
                        <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <h6 className="font-medium text-gray-800 dark:text-white">{candidate.name}</h6>
                            <span className="px-2 py-1 bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200 rounded-full text-xs font-medium">
                              {candidate.status}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">{candidate.email}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      case "checklist":
        return (
          <div className="bg-gradient-to-br from-white to-blue-50 dark:from-gray-900 dark:to-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 p-6 sm:p-8 w-full mx-auto space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center pb-4 border-b border-gray-200 dark:border-gray-600">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                <h4 className="text-2xl font-semibold text-gray-800 dark:text-white">Checklist</h4>
              </div>
              <button
                onClick={() => setActiveTab("overview")}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-2xl transition-colors duration-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full w-8 h-8 flex items-center justify-center"
                aria-label="Close Checklist"
              >
                ✕
              </button>
            </div>

            {/* Checklist Display */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-100 dark:border-gray-600">
              {checklist === null ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl">📋</span>
                  </div>
                  <p className="text-gray-500 dark:text-gray-400">No checklist found for this campaign.</p>
                </div>
              ) : checklist.length > 0 ? (
                <div className="space-y-3">
                  <h5 className="font-semibold text-gray-700 dark:text-gray-300 mb-4">Tasks ({checklist.length})</h5>
                  <ul className="space-y-3">
                    {checklist.map((item, index) => (
                      <li key={item.id} className="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors duration-200">
                        <div className="w-2 h-2 bg-green-500 rounded-full mr-3 flex-shrink-0"></div>
                        <span className="text-gray-700 dark:text-gray-300">{item.name}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                  <p className="text-gray-500 dark:text-gray-400">Loading checklist...</p>
                </div>
              )}
            </div>

            {/* Add New Item */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-100 dark:border-gray-600">
              <h5 className="font-semibold text-gray-700 dark:text-gray-300 mb-4">Add New Task</h5>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <input
                  type="text"
                  value={newChecklistItem}
                  onChange={(e) => setNewChecklistItem(e.target.value)}
                  placeholder="Add new checklist item"
                  title="Add new checklist item"
                  className="flex-1 dark:bg-gray-700 dark:text-white dark:border-gray-600 border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                />
                <button
                  onClick={addChecklistItem}
                  disabled={!newChecklistItem.trim()}
                  className={`px-6 py-3 rounded-lg text-white font-medium transition-all duration-200 
                    ${newChecklistItem.trim()
                      ? "bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                      : "bg-gray-400 cursor-not-allowed opacity-60"
                    }`}
                >
                  Add Task
                </button>
              </div>
            </div>
          </div>
        );
      case "technical-assessment":
        return (
          <div className="bg-gradient-to-br from-white to-purple-50 dark:from-gray-900 dark:to-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 p-6 sm:p-8 w-full mx-auto space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center pb-4 border-b border-gray-200 dark:border-gray-600">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-purple-500 rounded-full mr-3"></div>
                <h4 className="text-2xl font-semibold text-gray-800 dark:text-white">Technical Assessment</h4>
              </div>
              <button
                onClick={() => setActiveTab("overview")}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-2xl transition-colors duration-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full w-8 h-8 flex items-center justify-center"
                aria-label="Close Technical Assessment"
              >
                ✕
              </button>
            </div>

            {/* Assessment List */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-100 dark:border-gray-600">
              {technicalAssessments === null ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl">🔍</span>
                  </div>
                  <p className="text-gray-500 dark:text-gray-400">No technical assessments found for this campaign.</p>
                </div>
              ) : technicalAssessments.length > 0 ? (
                <div className="space-y-3">
                  <h5 className="font-semibold text-gray-700 dark:text-gray-300 mb-4">Assessments ({technicalAssessments.length})</h5>
                  <ul className="space-y-4">
                    {technicalAssessments.map((assessment) => (
                      <li key={assessment.id} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors duration-200">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                          <div className="flex items-center">
                            <div className="w-2 h-2 bg-purple-500 rounded-full mr-3 flex-shrink-0"></div>
                            <span className="font-medium text-gray-800 dark:text-gray-200">{assessment.name}</span>
                          </div>
                          <a
                            href={assessment.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all duration-200 shadow-sm hover:shadow-md text-sm font-medium"
                          >
                            <span className="mr-2">🔗</span>
                            Open Assessment
                          </a>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                  <p className="text-gray-500 dark:text-gray-400">Loading assessments...</p>
                </div>
              )}
            </div>

            {/* Add New Assessment */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-100 dark:border-gray-600">
              <h5 className="font-semibold text-gray-700 dark:text-gray-300 mb-4">Add New Assessment</h5>
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input
                    type="text"
                    value={newAssessment.name}
                    onChange={(e) => setNewAssessment({ ...newAssessment, name: e.target.value })}
                    placeholder="Assessment Name"
                    title="Assessment Name"
                    className="dark:bg-gray-700 dark:text-white dark:border-gray-600 border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                  />
                  <input
                    type="text"
                    value={newAssessment.link}
                    onChange={(e) => setNewAssessment({ ...newAssessment, link: e.target.value })}
                    placeholder="Assessment Link"
                    title="Assessment Link"
                    className="dark:bg-gray-700 dark:text-white dark:border-gray-600 border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                  />
                </div>
                <button
                  onClick={addTechnicalAssessment}
                  disabled={!newAssessment.name.trim() || !newAssessment.link.trim()}
                  className={`w-full sm:w-auto px-6 py-3 rounded-lg text-white font-medium transition-all duration-200
                    ${newAssessment.name.trim() && newAssessment.link.trim()
                      ? "bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                      : "bg-gray-400 cursor-not-allowed opacity-60"
                    }`}
                >
                  Add Assessment
                </button>
              </div>
            </div>
          </div>
        );
      case "candidates":
        return (
          <div className="bg-gradient-to-br from-white to-green-50 dark:from-gray-900 dark:to-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mx-auto">
            <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-200 dark:border-gray-600">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                <h4 className="text-2xl font-semibold text-gray-800 dark:text-white">Applicant</h4>
              </div>
              <button
                onClick={() => setActiveTab("overview")}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-2xl transition-colors duration-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full w-8 h-8 flex items-center justify-center"
              >
                ✕
              </button>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-gray-500 dark:text-gray-400">Loading candidates...</p>
              </div>
            ) : candidates?.length > 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 dark:border-gray-600 overflow-hidden">
                <ReusableTable
                  data={candidates}
                  columns={(() => {
                    const baseColumns = [
                      { label: "Name", field: "name" },
                      { label: "Email", field: "email" },
                      {
                        label: "Resume",
                        field: "resume_path",
                        render: (row) =>
                          row.resume_path ? (
                            <a
                              id={`resume-${row.id}`}
                              href={`https://hrmsnew.prepstripe.com/storage/${row.resume_path}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-3 py-1 bg-blue-100 hover:bg-blue-200 dark:bg-blue-900 dark:hover:bg-blue-800 text-blue-700 dark:text-blue-300 rounded-lg text-sm font-medium transition-colors duration-200"
                            >
                              📄 View Resume
                            </a>
                          ) : (
                            <span className="text-gray-400 italic">NA</span>
                          ),
                      },
                      { 
                        label: "Status", 
                        field: "status",
                        render: (row) => {
                          let bg = "bg-gray-100 dark:bg-gray-900";
                          let text = "text-gray-800 dark:text-gray-200";
                          if (row.status === "Shortlisted") {
                            bg = "bg-yellow-100 dark:bg-yellow-900";
                            text = "text-yellow-800 dark:text-yellow-200";
                          } else if (row.status === "Selected") {
                            bg = "bg-green-100 dark:bg-green-900";
                            text = "text-green-800 dark:text-green-200";
                          } else if (row.status === "Rejected") {
                            bg = "bg-red-100 dark:bg-red-900";
                            text = "text-red-800 dark:text-red-200";
                          } else if (row.status === "Pending") {
                            bg = "bg-yellow-100 dark:bg-yellow-900";
                            text = "text-yellow-800 dark:text-yellow-200";
                          }
                          return (
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${bg} ${text}`}>
                              {row.status}
                            </span>
                          );
                        }
                      },
                    
                      // Only add View Submission column if user is HR
                      ...(isHR ? [{
                        label: "View Submission",
                        field: "view_submission",
                        render: (row) => (
                          // console.log("row for view submission: ", row),
                          row?.campaign?.application_form_id && row?.application_form_submission?.submission_id ? (
                            <Link
                              to={`${getRoleBasedRoute("/admin/form_response", roles)}?form_id=${row.campaign.application_form_id}&submission_id=${row.application_form_submission.submission_id}`}
                              className="inline-flex items-center px-3 py-1 bg-blue-100 hover:bg-blue-200 dark:bg-blue-900 dark:hover:bg-blue-800 text-blue-700 dark:text-blue-300 rounded-lg text-sm font-medium transition-colors duration-200"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              View Submission
                            </Link>
                          ) : (
                            <span className="text-gray-400 italic">NA</span>
                          )
                        )
                      }] : []),
                    ];

                    // Only add Status Actions column if user is HR
                      if (isHR) {
                        baseColumns.push({
                          label: "Status Actions",
                          field: "status_actions",
                          className: "w-48", // Fixed width for status actions column
                          render: (row) => {
                            if (row.status === "Pending") {
                              return (
                                <div className="flex justify-center items-center gap-2 w-full">
                                  <button
                                    className="bg-gradient-to-r from-green-600 to-green-700 text-white px-3 py-2 rounded-lg hover:from-green-700 hover:to-green-800 transition-all duration-200 shadow-sm hover:shadow-md font-medium text-sm whitespace-nowrap"
                                    onClick={() => handleShortlistCandidate(row.id)}
                                    aria-label={`Shortlist ${row.name}`}>
                                    ✓ Shortlist
                                  </button>
                                  <button
                                    className="bg-gradient-to-r from-red-600 to-red-700 text-white px-3 py-2 rounded-lg hover:from-red-700 hover:to-red-800 transition-all duration-200 shadow-sm hover:shadow-md font-medium text-sm whitespace-nowrap"
                                    onClick={() => handleRejectCandidate(row.id)}
                                    aria-label={`Reject ${row.name}`}>
                                    ✕ Reject
                                  </button>
                                </div>
                              );
                            } else if (row.status === "Shortlisted") {
                              return (
                                <div className="flex justify-center items-center w-full">
                                  <span className="px-3 py-2 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-lg font-medium text-sm whitespace-nowrap min-w-[120px] text-center">
                                    ✓ Shortlisted
                                  </span>
                                </div>
                              );
                            } else if (row.status === "Rejected") {
                              return (
                                <div className="flex justify-center items-center w-full">
                                  <span className="px-3 py-2 bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 rounded-lg font-medium text-sm whitespace-nowrap min-w-[120px] text-center">
                                    ✕ Rejected
                                  </span>
                                </div>
                              );
                            } else {
                              return (
                                <div className="flex justify-center items-center w-full">
                                  <span className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg font-medium text-sm whitespace-nowrap min-w-[120px] text-center">
                                    {row.status}
                                  </span>
                                </div>
                              );
                            }
                          },
                        });

                        // Add Interview Actions column
                        baseColumns.push({
                          label: "Interview Actions",
                          field: "interview_actions",
                          className: "w-48", // Fixed width for interview actions column
                          render: (row) => {
                            if (row.status === "Shortlisted") {
                              if (!row.interviews?.some(interview => interview.status === "Scheduled")) {
                                return (
                                  <div className="flex justify-center items-center w-full">
                                    <button
                                      className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-2 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-sm hover:shadow-md font-medium text-sm whitespace-nowrap"
                                      onClick={() => openInterviewModal(row)}
                                      aria-label={`Schedule Interview for ${row.name}`}>
                                      📅 Schedule Interview
                                    </button>
                                  </div>
                                );
                              } else {
                                return (
                                  <div className="flex justify-center items-center w-full">
                                    <span className="px-3 py-2 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-lg font-medium text-sm whitespace-nowrap min-w-[180px] text-center">
                                      📅 Interview Scheduled
                                    </span>
                                  </div>
                                );
                              }
                            } else if (row.status === "Pending") {
                              return (
                                <div className="flex justify-center items-center w-full">
                                  <span className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-lg font-medium text-sm whitespace-nowrap min-w-[180px] text-center">
                                    Review Required
                                  </span>
                                </div>
                              );
                            } else if (row.status === "Rejected") {
                              return (
                                <div className="flex justify-center items-center w-full">
                                  <span className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-lg font-medium text-sm whitespace-nowrap min-w-[183px] text-center">
                                    Not Available
                                  </span>
                                </div>
                              );
                            } else {
                              return (
                                <div className="flex justify-center items-center w-full">
                                  <span className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-lg font-medium text-sm whitespace-nowrap min-w-[183px] text-center">
                                    Not Available
                                  </span>
                                </div>
                              );
                            }
                          },
                        });
                      }

                    return baseColumns;
                  })()}
                  filterable={true}
                />

                {/* Pagination Controls */}
                <div className="flex justify-center items-center gap-4 p-6 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="flex items-center px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                  >
                    ← Previous
                  </button>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Page</span>
                    <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-lg font-medium">
                      {currentPage}
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">of {totalPages}</span>
                  </div>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="flex items-center px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                  >
                    Next →
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg">
                <div className="w-20 h-20 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">👥</span>
                </div>
                <p className="text-gray-500 dark:text-gray-400 text-lg">No candidates available.</p>
              </div>
            )}
          </div>
        );
      case "interviews":
        return (
          <div className="bg-gradient-to-br from-white to-indigo-50 dark:from-gray-900 dark:to-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 p-6 sm:p-8 w-full mx-auto">
            {/* Header */}
            <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-200 dark:border-gray-600">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-indigo-500 rounded-full mr-3"></div>
                <h4 className="text-2xl font-semibold text-gray-800 dark:text-white">Interviews</h4>
              </div>
              <button
                onClick={() => setActiveTab("overview")}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-2xl transition-colors duration-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full w-8 h-8 flex items-center justify-center"
                aria-label="Close">
                ✕
              </button>
            </div>

            {/* Interview List */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 dark:border-gray-600">
              {campaign.interviews?.length > 0 ? (
                <div className="divide-y divide-gray-200 dark:divide-gray-600">
                  {campaign.interviews.map((interview, index) => (
                    <div
                      key={index}
                      className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                        
                        <div className="flex items-center">
                          <div className="w-2 h-2 bg-indigo-500 rounded-full mr-3 flex-shrink-0"></div>
                          <div>
                            <p className="text-sm text-gray-600 dark:text-gray-400 font-medium mb-1">Candidate</p>
                            <p className="text-gray-800 dark:text-gray-200 font-semibold">{interview.candidate_name}</p>
                          </div>
                        </div>

                        <div className="flex items-center">
                          <div className="w-2 h-2 bg-blue-500 rounded-full mr-3 flex-shrink-0"></div>
                          <div>
                            <p className="text-sm text-gray-600 dark:text-gray-400 font-medium mb-1">Interviewer</p>
                            <p className="text-gray-800 dark:text-gray-200 font-semibold">{interview.interviewer}</p>
                          </div>
                        </div>

                        <div className="flex items-center">
                          <div className="w-2 h-2 bg-green-500 rounded-full mr-3 flex-shrink-0"></div>
                          <div>
                            <p className="text-sm text-gray-600 dark:text-gray-400 font-medium mb-1">Status</p>
                            <span className="px-3 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-full text-sm font-medium">
                              {interview.status}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center">
                          <div className="w-2 h-2 bg-purple-500 rounded-full mr-3 flex-shrink-0"></div>
                          <div>
                            <p className="text-sm text-gray-600 dark:text-gray-400 font-medium mb-1">Scheduled Time</p>
                            <p className="text-gray-800 dark:text-gray-200 font-semibold">
                              {interview.scheduled_at ? new Date(interview.scheduled_at).toLocaleString() : 'Not scheduled'}
                            </p>
                          </div>
                        </div>

                        {/* Interview Link or Location */}
                        {(interview.interview_link || interview.location) && (
                          <div className="">
                            <div className="flex items-center">
                              <div className="w-2 h-2 bg-yellow-500 rounded-full mr-3 flex-shrink-0"></div>
                              <div className="flex-1">
                                <p className="text-sm text-gray-600 dark:text-gray-400 font-medium mb-1">
                                  {interview.interview_link ? 'Interview Link' : 'Location'}
                                </p>
                                {interview.interview_link ? (
                                  <button
                                    onClick={() => {
                                      const link = document.createElement('a');
                                      link.href = interview.interview_link;
                                      link.target = '_blank';
                                      link.rel = 'noopener noreferrer';
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                    }}
                                    className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium underline flex items-center gap-2"
                                  >
                                    🔗 Join Interview
                                  </button>
                                ) : (
                                  <p className="text-gray-800 dark:text-gray-200 font-semibold">{interview.location}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-3xl">🎤</span>
                  </div>
                  <p className="text-gray-500 dark:text-gray-400 text-lg">No Interviews available.</p>
                </div>
              )}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* Notification if exists */}
      {notification && (
        <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
      )}

      {/* Sticky Container for both Header and Tabs */}
      <div className="z-30 bg-white dark:bg-gray-900 shadow-sm border-b border-gray-200 dark:border-gray-700">
        {/* Sticky Header */}
        <div className="p-6 sm:p-6 bg-gray-50 text-black rounded-t-xl">
          <div className="flex items-center justify-between">
            <h3 className="text-2xl sm:text-3xl font-semibold mb-4 break-words">
              {campaign.campaign_name}
            </h3>
            <button
              onClick={close}
              className="text-gray-500 dark:hover:text-gray-400 hover:text-gray-800 text-xl m-2">
              ✖
            </button>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-black font-medium">Status:</span> 
                <span className={`px-2 py-1 rounded-full ${campaign.status === 'Expired' ? 'text-red-900 bg-red-100' : 'text-green-900 bg-green-100'}`}>{campaign.status}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-black font-medium">Position:</span> 
                <span className="font-semibold text-gray-600">{campaign.position}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-black font-medium">Salary:</span> 
                <span className="font-semibold text-gray-600">₹{campaign.ctc}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Tabs */}
        <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
          <div className="flex min-w-max">
            {[
              { key: "overview", label: "Overview", color: "blue" },
              { key: "candidates", label: "Applicants", color: "orange" },
              { key: "technical-assessment", label: "Technical Assessment", color: "purple" },
              { key: "interviews", label: "Interviews", color: "indigo" },
              { key: "summary", label: "Summary", color: "green" },
            ].map((tab) => {
              const isActive = activeTab === tab.key;
              const getActiveClasses = () => {
                switch (tab.color) {
                  case "blue":
                    return "border-blue-500 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20";
                  case "orange":
                    return "border-orange-500 text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20";
                  case "purple":
                    return "border-purple-500 text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20";
                  case "indigo":
                    return "border-indigo-500 text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20";
                  default:
                    return "border-gray-500 text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/20";
                }
              };

              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`py-4 px-6 text-sm sm:text-base font-medium transition-all duration-300 border-b-2 whitespace-nowrap flex items-center gap-2 ${
                    isActive
                      ? getActiveClasses()
                      : "border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600"
                  }`}>
                  <span className="text-lg">{tab.icon}</span>
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Scrollable Tab Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {renderTabContent()}
      </div>

      {/* Interview Scheduling Modal */}
      <Modal
        isOpen={isInterviewModalOpen}
        onRequestClose={closeInterviewModal}
        title="Schedule Interview"
      >
        {selectedCandidateForInterview && (
          <InterviewForm
            initialValues={{
              candidate_id: selectedCandidateForInterview.id,
              interviewer: "",
              scheduled_at: "",
              status: "Scheduled",
              interview_type: "",
              interview_link: "",
              location: "",
            }}
            onSubmit={handleInterviewSubmit}
            closeModal={closeInterviewModal}
          />
        )}
      </Modal>
    </div>
  );
};

export default CampaignDetails;